/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Measure from 'react-measure';
import { Tooltip } from '@ba/ba-ui-carbon-toolkit';
import { Formatter } from '../../features/compare/FormatterWrapper';

const PADDING = 10;

export default class CompareTableDynamicCell extends Component {
	static propTypes = {
		value: PropTypes.string.isRequired,
		valueUse: PropTypes.number,
		formatSpec: PropTypes.object,
		className: PropTypes.string,
		tooltipValue: PropTypes.string
	};

	state = {
		charCount: this._handleValueType().length
	}

	componentDidMount() {
		if (!this._textNode.offsetWidth) {
			this._getTextWidth();
		} else {
			this._textNode && this._getTextWidth();
		}
	}

	componentDidUpdate(prevProps, prevState) {
		if (this.state._isMounted) {
			const containerWidth = this._node.offsetWidth;
			this._textWidth = this._textNode.offsetWidth;

			if (prevState.charCount !== this.state.charCount) {
				// check if resizing is still needed after first render
				this._compareTextAndContainerWidth(containerWidth);
			}
		} else if (prevProps.value !== this.props.value) {
			// trigger measuring after updating props
			this._getTextWidth();
		}
	}

	onMeasure = (dimensions) => {
		this.containerWidth = Math.ceil(dimensions.entry.width);
		this._getTextWidth();
		this._compareTextAndContainerWidth(this.containerWidth);
	}


	_handleValueType(value = this.props.value) {
		return value === null ? '' : String(value);
	}

	_getTextWidth = () => {
		if (this._textNode) {
			this._textWidth = this._textNode.offsetWidth;
			this.setState({ _isMounted: true });
		}
	}

	_compareTextAndContainerWidth(containerWidth) {
		// check to see if our text fits within our container minus a 10 pixel "padding"
		this.willTruncate = containerWidth - PADDING < this._textWidth;
	}

	_getTruncValue = () => {
		const formatSpec = {
			...this.props.formatSpec,
			decimalFormatLength: 'short',
			maximumFractionDigits: 2
		};
		/* The Formatter we use does not support abbreviation of numbers as a percent. For the mean time we will not see
		 * percent formatted values be truncated but hopefully in R5 we will be able to get this added to the Formatter.
		 */
		// If our valueUse is defined, we want to truncate it. If not, we use the display value.
		if (this.props.valueUse) {
			return Formatter.format(this.props.valueUse, formatSpec);
		} else {
			return this.props.value;
		}
	}

	_ref = (node) => {
		this._node = node;
		this._measureRef(node);
	}

	_refText = (node) => {
		this._textNode = node;
	}

	getDefaultNode = (className) => {
		return (
			<span role="cell" className={className} ref={this._refText}>{this.props.value}</span>
		);
	}

	getTruncNode = () => {
		return (
			<span role="cell" ref={this._refText}>{this._getTruncValue()}</span>
		);
	}

	_render() {
		const className = 'dynamicCell ' + this.props.className;
		const truncClassName = 'truncated-' + className;
		this._compareTextAndContainerWidth(this.containerWidth);

		// check if we are supposed to display a truncated value or not
		if (!this.willTruncate) {
			// check if our value has a tooltip associated with it
			if (this.props.tooltipValue) {
				return (
					<Tooltip className={className} title={this.props.tooltipValue}>
						{ this.getDefaultNode(className) }
					</Tooltip>
				);
			} else {
				return this.getDefaultNode(className);
			}
		} else {
			if (this.props.tooltipValue) {
				return (
					<Tooltip className={truncClassName} title={this.props.tooltipValue}>
						{ this.getTruncNode() }
					</Tooltip>
				);
			} else {
				return (
					<Tooltip className={truncClassName} title={this.props.value}>
						{ this.getTruncNode() }
					</Tooltip>
				);
			}
		}
	}

	render() {
		return (
			<Measure onResize={this.onMeasure}>
				{({ measureRef }) => {
					this._measureRef = measureRef;
					return (
						<div ref={this._ref}>
							{this._render()}
						</div>
					);
				}}
			</Measure>
		);
	}
}



// WEBPACK FOOTER //
// ./src/js/components/lwstats/CompareTableDynamicCell.js